import {
  Box,
  VStack,
  Text,
  Heading,
  HStack,
  Badge,
  Flex,
  Icon,
  Button,
  Separator,
} from "@chakra-ui/react";
import { ChangeEventHandler } from "react";
import ResultStudentsTable from "./ResultStudentsTable";
import { Student } from "../../../services/Dtos";
import { useStyles } from "../../../hooks/useStyles";
import useThemeColors from "../../../hooks/useThemeColors";
import Cad from "../../CustomUi/Cad";
import useIcons from "../../../hooks/useIcons";
import CustomMenu from "../../CustomUi/CustomMenu";
import SelectInput from "../../CustomUi/SelectInput";
import TextInput from "../../CustomUi/TextInput";
import { useRoleAccess } from "../../../hooks/useRoleAccess";
import { Role } from "../../../services/Enums";
import { useBreakPoint } from "../../../hooks/useBreakPoint";

interface StudentsPanelProps {
  selectedClassId: string;
  students: Student[] | undefined;
  chosenStudent: Student | null;
  handleRowClick: (student: Student | null) => void;
  // Filter controls props
  searchQuery: string;
  handleSearchChange: ChangeEventHandler<HTMLInputElement>;
  selectedStatus: number;
  handleStatusChange: ChangeEventHandler<HTMLSelectElement>;
  handleClassChange: ChangeEventHandler<HTMLSelectElement>;
  classes: any[];
  // Bulk actions props
  onDownloadTemplate: () => void;
  onBulkUploadOpen: () => void;
  isActionLoading: boolean;
}

function StudentsPanel({
  selectedClassId,
  students,
  chosenStudent,
  handleRowClick,
  searchQuery,
  handleSearchChange,
  selectedStatus,
  handleStatusChange,
  handleClassChange,
  classes,
  onDownloadTemplate,
  onBulkUploadOpen,
  isActionLoading,
}: StudentsPanelProps) {
  const { scrollBar } = useStyles();
  const { isMobile } = useBreakPoint();
  const colors = useThemeColors();
  const { hasRoleOrHigher } = useRoleAccess();

  //todo consolidate students statuses in constants
  const statusOptions = [
    { value: 0, label: "Active", color: "green" },
    { value: 1, label: "Inactive", color: "gray" },
    { value: 2, label: "Suspended", color: "orange" },
    { value: 3, label: "Expelled", color: "red" },
    { value: 4, label: "Graduated", color: "blue" },
  ];

  const selectedClass = classes.find((c) => c.id === selectedClassId);
  const selectedStatusInfo = statusOptions.find(
    (s) => s.value === selectedStatus
  );
  const icons = useIcons();

  return (
    <Cad h="100%" maxW={{ base: "100%", lg: "450px" }} px={"0px"} py={"0px"}>
      <VStack gap={0} align="stretch" h="100%">
        <Box
          p={5}
          bg={colors.background.secondary}
          borderBottom="1px solid"
          borderBottomColor={colors.border.primary}
          flexShrink={0}
        >
          <VStack gap={2} align="stretch">
            {/* Header with Icon */}
            <HStack justify="space-between" align="center">
              <VStack gap={0} align="start" w={"full"}>
                <HStack
                  gap={2}
                  align="center"
                  justify="space-between"
                  w={"full"}
                >
                  <HStack gap={3} align="center">
                    <Icon
                      as={icons.people.students}
                      boxSize={5}
                      color={colors.schoolAccent.icon}
                    />
                    <Heading size="md">Students</Heading>
                  </HStack>
                  {hasRoleOrHigher(Role.Editor) && (
                    <CustomMenu
                      trigger={
                        <Button
                          as={Button}
                          size="sm"
                          variant="ghost"
                          loading={isActionLoading}
                          _hover={{
                            transform: "translateY(-1px)",
                          }}
                          _active={{
                            transform: "translateY(0)",
                          }}
                          transition="all 0.2s"
                        >
                          <Icon as={icons.utility.chevronDown} />
                          Actions
                        </Button>
                      }
                      menuItems={[
                        {
                          label: "Download Template",
                          value: "downloadTemplate",
                          icon: icons.utility.download,
                          onClick: onDownloadTemplate,
                        },
                        {
                          label: "Import Results",
                          value: "bulkUpload",
                          icon: icons.utility.upload,
                          onClick: onBulkUploadOpen,
                        },
                      ]}
                    />
                  )}
                </HStack>
                <Text opacity={0.7} fontSize={"xs"} p={0}>
                  {students?.length || 0} available
                </Text>
              </VStack>
            </HStack>

            <Separator />

            {/* Filter Controls */}
            <VStack gap={3} align="stretch">
              {/* Class and Status Filters */}
              <Flex direction={isMobile ? "column" : "row"} gap={3}>
                <SelectInput
                  collection={classes.map((cls) => ({
                    label: cls.className,
                    value: cls.id,
                  }))}
                  value={selectedClassId}
                  onChange={handleClassChange}
                  name="class"
                  placeholder="Select Class"
                />

                <SelectInput
                  collection={statusOptions.map((status) => ({
                    label: status.label,
                    value: status.value.toString(),
                  }))}
                  value={selectedStatus.toString()}
                  onChange={handleStatusChange}
                  name="status"
                  placeholder="Select Status"
                />
              </Flex>

              {/* Search Input */}

              <TextInput
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search Students"
                name="search"
                endElement={<Icon as={icons.utility.search} />}
              />

              {/* Active Filters Display */}
              {(selectedClass || selectedStatusInfo) && (
                <HStack gap={2} wrap="wrap">
                  {selectedClass && (
                    <Badge colorPalette="blue" variant="subtle" fontSize="2xs">
                      Class: {selectedClass.className}
                    </Badge>
                  )}
                  {selectedStatusInfo && (
                    <Badge
                      colorPalette={selectedStatusInfo.color as any}
                      variant="subtle"
                      fontSize="2xs"
                    >
                      Status: {selectedStatusInfo.label}
                    </Badge>
                  )}
                  {searchQuery && (
                    <Badge colorPalette="brand" variant="subtle" fontSize="2xs">
                      Search: "{searchQuery}"
                    </Badge>
                  )}
                </HStack>
              )}
            </VStack>
          </VStack>
        </Box>

        {/* Students List */}
        <Box flex={1} overflowY="auto" minH={0} css={scrollBar}>
          <ResultStudentsTable
            selectedClassId={selectedClassId}
            students={students || null}
            chosenStudent={chosenStudent}
            handleRowClick={handleRowClick}
          />
        </Box>
      </VStack>
    </Cad>
  );
}

export default StudentsPanel;
